import React from 'react';
import { motion } from 'framer-motion';
import { TrendUp, TrendDown, Trophy, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatPercent } from '../lib/utils';
import { useCurrency } from '../context/CurrencyContext';

const PortfolioTable = ({ holdings = [] }) => {
  const navigate = useNavigate();
  const { format: fmtCcy } = useCurrency();

  if (!holdings.length) return null;

  // Sort holdings by profit/loss percent for "Top Gainers" highlight
  const sorted = [...holdings].sort((a, b) => (b.profit_loss_percent || 0) - (a.profit_loss_percent || 0));
  const topGainers = sorted.filter(h => (h.profit_loss_percent || 0) > 0).slice(0, 3);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="mb-12"
      data-testid="portfolio-table-section"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-unbounded font-bold text-white">Your Holdings</h2>
          <span className="text-xs text-zinc-500 font-mono">({holdings.length})</span>
        </div>
        <button
          onClick={() => navigate('/portfolio')}
          className="text-gold hover:text-gold-light font-bold transition-colors text-sm flex items-center gap-1"
          data-testid="view-all-holdings"
        >
          Full Portfolio <ArrowRight size={14} />
        </button>
      </div>

      {/* Top Gainers strip */}
      {topGainers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 flex-wrap mb-4 p-3 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20"
          data-testid="top-gainers-strip"
        >
          <div className="flex items-center gap-2">
            <Trophy size={18} weight="fill" className="text-emerald-400" />
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-300">Top Gainers</span>
          </div>
          {topGainers.map((h) => (
            <button
              key={h.symbol}
              onClick={() => navigate(`/market/${h.symbol}`)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 transition-colors"
              data-testid={`top-gainer-${h.symbol}`}
            >
              <span className="text-white font-bold text-sm">{h.symbol}</span>
              <span className="text-emerald-400 text-xs font-mono font-bold">+{formatPercent(h.profit_loss_percent)}</span>
            </button>
          ))}
        </motion.div>
      )}

      {/* Mobile cards (sm and below) */}
      <div className="grid grid-cols-1 sm:hidden gap-3">
        {sorted.map((h) => (
          <button
            key={h.symbol}
            onClick={() => navigate(`/market/${h.symbol}`)}
            className="glass-card p-4 text-left hover:scale-[1.01] transition-transform"
            data-testid={`mobile-holding-${h.symbol}`}
          >
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="font-unbounded font-bold text-white">{h.symbol}</p>
                <p className="text-xs text-zinc-400 truncate max-w-[180px]">{h.company_name || h.symbol}</p>
              </div>
              <div className="text-right">
                <p className="font-mono font-bold text-white">{fmtCcy(h.total_value)}</p>
                <p className={`text-xs font-mono font-bold ${h.profit_loss >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {h.profit_loss >= 0 ? '+' : ''}{fmtCcy(h.profit_loss)} ({formatPercent(h.profit_loss_percent)})
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="portfolio-table">
            <thead className="border-b border-white/10 bg-white/5">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-zinc-400 uppercase tracking-wide">Symbol</th>
                <th className="text-left p-3 text-xs font-medium text-zinc-400 uppercase tracking-wide hidden md:table-cell">Company</th>
                <th className="text-right p-3 text-xs font-medium text-zinc-400 uppercase tracking-wide">Shares</th>
                <th className="text-right p-3 text-xs font-medium text-zinc-400 uppercase tracking-wide hidden lg:table-cell">Avg Buy</th>
                <th className="text-right p-3 text-xs font-medium text-zinc-400 uppercase tracking-wide">Price</th>
                <th className="text-right p-3 text-xs font-medium text-zinc-400 uppercase tracking-wide">Value</th>
                <th className="text-right p-3 text-xs font-medium text-zinc-400 uppercase tracking-wide">P/L</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((h) => {
                const positive = (h.profit_loss || 0) >= 0;
                return (
                  <tr
                    key={h.symbol}
                    onClick={() => navigate(`/market/${h.symbol}`)}
                    className="border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-colors"
                    data-testid={`portfolio-row-${h.symbol}`}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        {positive
                          ? <TrendUp size={14} weight="fill" className="text-success" />
                          : <TrendDown size={14} weight="fill" className="text-destructive" />}
                        <span className="font-bold text-white">{h.symbol}</span>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <span className="text-sm text-zinc-300 truncate block max-w-[220px]">{h.company_name || h.symbol}</span>
                    </td>
                    <td className="p-3 text-right font-mono text-sm text-white">{Number(h.shares || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-mono text-sm text-zinc-400 hidden lg:table-cell">{fmtCcy(h.avg_buy_price)}</td>
                    <td className="p-3 text-right font-mono text-sm text-white">{fmtCcy(h.current_price)}</td>
                    <td className="p-3 text-right font-mono text-sm font-bold text-white">{fmtCcy(h.total_value)}</td>
                    <td className="p-3 text-right">
                      <div className={`font-mono font-bold text-sm ${positive ? 'text-success' : 'text-destructive'}`}>
                        {positive ? '+' : ''}{fmtCcy(h.profit_loss)}
                      </div>
                      <div className={`font-mono text-xs ${positive ? 'text-success/70' : 'text-destructive/70'}`}>
                        {formatPercent(h.profit_loss_percent)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default PortfolioTable;
